import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Grid, Typography, Button, CircularProgress, Card, CardContent, Box,
  Dialog, DialogTitle, DialogContent, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  makeStyles
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import RefreshIcon from '@material-ui/icons/Refresh';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import html2pdf from 'html2pdf.js';
import { KPICard, TrendChart, FunnelChart, RankingTable, AdoptionTable, PeriodSelector, FrequencyBarChart, CohortRetentionCard, MonthlyCohortTable } from '../../components/dashboard';
import { useNavigate } from 'react-router-dom';
import { dashboardService } from '../../services/dashboardService';
import Navbar from '../../components/Navbar';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
  },
  container: {
    maxWidth: 1400,
    margin: '0 auto',
    padding: theme.spacing(4),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(4),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  headerTitle: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  refreshButton: {
    backgroundColor: '#1e293b',
    color: '#ffffff',
    borderRadius: 12,
    padding: '10px 20px',
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#334155',
    },
    '&:disabled': {
      backgroundColor: '#94a3b8',
    },
  },
  pdfButton: {
    backgroundColor: '#059669',
    color: '#ffffff',
    borderRadius: 12,
    padding: '10px 20px',
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: '#047857',
    },
    '&:disabled': {
      backgroundColor: '#94a3b8',
    },
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#334155',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '&::before': {
      content: '""',
      display: 'block',
      width: 4,
      height: 24,
      backgroundColor: '#059669',
      borderRadius: 2,
    },
  },
  card: {
    borderRadius: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0',
    transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
    '&:hover': {
      boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)',
      transform: 'translateY(-2px)',
    },
  },
  cardContent: {
    padding: theme.spacing(3),
  },
  errorCard: {
    backgroundColor: '#fef2f2',
    borderColor: '#fecaca',
  },
  statsCard: {
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    color: '#ffffff',
    borderRadius: 16,
  },
  statsLabel: {
    fontSize: '0.875rem',
    color: '#94a3b8',
    marginBottom: theme.spacing(0.5),
  },
  statsValue: {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#ffffff',
  },
  statsVariation: {
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  positiveVariation: {
    color: '#4ade80',
  },
  negativeVariation: {
    color: '#f87171',
  },
}));

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(value);
};

const getPeriodLabel = (period) => {
  switch (period) {
    case 'week':
      return 'sem';
    case 'month':
      return 'mois';
    case 'quarter':
      return 'trim';
    case 'custom':
      return '';
    default:
      return 'sem';
  }
};

const DashboardView = () => {
  const classes = useStyles();
  const dashboardRef = useRef(null);

  // État export PDF
  const [exportingPdf, setExportingPdf] = useState(false);

  // États données
  const [overview, setOverview] = useState(null);
  const [acquisition, setAcquisition] = useState(null);
  const [acquisitionCohort, setAcquisitionCohort] = useState(null);
  const [retention, setRetention] = useState(null);
  const [valueData, setValueData] = useState(null);
  const [restaurants, setRestaurants] = useState(null);
  const [b2bData, setB2bData] = useState(null);
  const [monthlyCohort, setMonthlyCohort] = useState(null);

  // États UI
  const [period, setPeriod] = useState('week');
  const [customDates, setCustomDates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // États loading individuels pour skeleton
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [acquisitionLoading, setAcquisitionLoading] = useState(true);
  const [acquisitionCohortLoading, setAcquisitionCohortLoading] = useState(true);
  const [retentionLoading, setRetentionLoading] = useState(true);
  const [valueLoading, setValueLoading] = useState(true);
  const [restaurantsLoading, setRestaurantsLoading] = useState(true);
  const [b2bLoading, setB2bLoading] = useState(true);
  const [monthlyCohortLoading, setMonthlyCohortLoading] = useState(true);

  // États erreur individuels
  const [overviewError, setOverviewError] = useState(null);
  const [acquisitionError, setAcquisitionError] = useState(null);
  const [acquisitionCohortError, setAcquisitionCohortError] = useState(null);
  const [retentionError, setRetentionError] = useState(null);
  const [valueError, setValueError] = useState(null);
  const [restaurantsError, setRestaurantsError] = useState(null);
  const [b2bError, setB2bError] = useState(null);
  const [monthlyCohortError, setMonthlyCohortError] = useState(null);

  // États pour la modal détails établissement
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const navigate = useNavigate();

  // Chargement de toutes les données
  const loadAllData = useCallback(async (selectedPeriod, selectedCustomDates = null) => {
    try {
      setLoading(true);
      setError(null);
      setOverviewLoading(true);
      setAcquisitionLoading(true);
      setAcquisitionCohortLoading(true);
      setRetentionLoading(true);
      setValueLoading(true);
      setRestaurantsLoading(true);
      setB2bLoading(true);
      setMonthlyCohortLoading(true);

      // Appels parallèles pour performance
      const [
        overviewRes,
        acquisitionRes,
        acquisitionCohortRes,
        retentionRes,
        valueRes,
        restaurantsRes,
        b2bRes,
        monthlyCohortRes
      ] = await Promise.all([
        dashboardService.getOverview(selectedPeriod, selectedCustomDates),
        dashboardService.getAcquisition(selectedPeriod, selectedCustomDates),
        dashboardService.getAcquisitionCohort(selectedPeriod, selectedCustomDates),
        dashboardService.getRetention(selectedPeriod, selectedCustomDates),
        dashboardService.getValue(selectedPeriod, selectedCustomDates),
        dashboardService.getRestaurants(selectedPeriod, selectedCustomDates),
        dashboardService.getB2B(selectedPeriod, selectedCustomDates),
        dashboardService.getMonthlyCohort(),
      ]);

      setOverview(overviewRes.data);
      console.log('overviewRes.data :' , overviewRes.data)
      setOverviewLoading(false);
      setOverviewError(null);

      setAcquisition(acquisitionRes.data);
      console.log('acquisitionRes.data :' , acquisitionRes.data)

      setAcquisitionLoading(false);
      setAcquisitionError(null);

      setAcquisitionCohort(acquisitionCohortRes.data);
      console.log('acquisitionCohortRes.data :' , acquisitionCohortRes.data)

      setAcquisitionCohortLoading(false);
      setAcquisitionCohortError(null);

      setRetention(retentionRes.data);
      console.log('retentionRes.data :' , retentionRes.data)

      setRetentionLoading(false);
      setRetentionError(null);

      setValueData(valueRes.data);
      setValueLoading(false);
      setValueError(null);

      setRestaurants(restaurantsRes.data);
      setRestaurantsLoading(false);
      setRestaurantsError(null);

      setB2bData(b2bRes.data);
        console.log('b2bRes.data :' , b2bRes.data)

      setB2bLoading(false);
      setB2bError(null);

      setMonthlyCohort(monthlyCohortRes.data);
      console.log('monthlyCohortRes.data :', monthlyCohortRes.data);
      setMonthlyCohortLoading(false);
      setMonthlyCohortError(null);

    } catch (err) {
      console.error('Erreur chargement dashboard:', err);
      setError('Erreur lors du chargement des données');
      setOverviewError('Erreur lors du chargement des données');
      setAcquisitionError('Erreur lors du chargement des données');
      setAcquisitionCohortError('Erreur lors du chargement des données');
      setRetentionError('Erreur lors du chargement des données');
      setValueError('Erreur lors du chargement des données');
      setRestaurantsError('Erreur lors du chargement des données');
      setB2bError('Erreur lors du chargement des données');
      setMonthlyCohortError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
      setOverviewLoading(false);
      setAcquisitionLoading(false);
      setAcquisitionCohortLoading(false);
      setRetentionLoading(false);
      setValueLoading(false);
      setRestaurantsLoading(false);
      setB2bLoading(false);
      setMonthlyCohortLoading(false);
    }
  }, []);

  // Chargement initial et sur changement de période
  useEffect(() => {
    // Ne pas charger si period=custom mais pas de dates définies
    if (period === 'custom' && (!customDates?.startDate || !customDates?.endDate)) {
      return;
    }
    loadAllData(period, customDates);
  }, [loadAllData, period, customDates]);

  // Handler changement période
  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    // Si on quitte le mode custom, on réinitialise les dates
    if (newPeriod !== 'custom') {
      setCustomDates(null);
    }
  };

  // Handler changement dates personnalisées
  const handleCustomDatesChange = (dates) => {
    setCustomDates(dates);
  };

  // Handler actualiser
  const handleRefresh = () => {
    loadAllData(period, customDates);
  };

  const handleRestaurantClick = async (row) => {
    setDetailsModalOpen(true);
    setDetailsLoading(true);
    setDetailsData(null);
    try {
      const data = await dashboardService.getEtablissementDetails(row.id, period);
      setDetailsData(data);
    } catch (err) {
      console.error('Erreur chargement détails établissement:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setDetailsData(null);
  };

  // Fonction export PDF
  const handleExportPdf = async () => {
    if (!dashboardRef.current || loading) return;

    setExportingPdf(true);

    try {
      const element = dashboardRef.current;
      const filename = `dashboard-csibon-${new Date().toISOString().split('T')[0]}.pdf`;

      // Sauvegarder les styles originaux des conteneurs avec overflow
      const overflowElements = element.querySelectorAll('[style*="overflow"], [class*="MuiTableContainer"]');
      const originalStyles = [];
      overflowElements.forEach((el, index) => {
        originalStyles[index] = {
          overflow: el.style.overflow,
          maxHeight: el.style.maxHeight,
          height: el.style.height
        };
        el.style.overflow = 'visible';
        el.style.maxHeight = 'none';
        el.style.height = 'auto';
      });

      const opt = {
        margin: [5, 5, 5, 5],
        filename: filename,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: {
          scale: 1.5,
          useCORS: true,
          logging: false,
          letterRendering: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'landscape'
        },
        pagebreak: { mode: ['css', 'legacy'], avoid: ['tr', '.MuiCard-root'] }
      };

      await html2pdf().set(opt).from(element).save();

      // Restaurer les styles originaux
      overflowElements.forEach((el, index) => {
        el.style.overflow = originalStyles[index].overflow;
        el.style.maxHeight = originalStyles[index].maxHeight;
        el.style.height = originalStyles[index].height;
      });
    } catch (err) {
      console.error('Erreur export PDF:', err);
    } finally {
      setExportingPdf(false);
    }
  };

  const placeholderStyle = {
    minHeight: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5'
  };

  const periodLabel = getPeriodLabel(period);

  // Formater les dates de la période
  const formatPeriodDates = () => {
    if (!overview?.periode) return null;
    const start = new Date(overview.periode.start);
    const end = new Date(overview.periode.end);
    const formatDate = (d) => d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  // Obtenir le label de comparaison selon la période
  const getComparisonLabel = (dataSource = overview) => {
    if (!dataSource?.periode_precedente) return null;
    const start = new Date(dataSource.periode_precedente.start);
    const end = new Date(dataSource.periode_precedente.end);
    const formatDate = (d) => d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    const prevDates = `${formatDate(start)} - ${formatDate(end)}`;

    switch (period) {
      case 'week':
        return `vs sem. précédente (${prevDates})`;
      case 'month':
        return `vs mois précédent (${prevDates})`;
      case 'quarter':
        return `vs trim. précédent (${prevDates})`;
      default:
        return `vs période précédente (${prevDates})`;
    }
  };

  const renderOverviewSection = () => {
    if (overviewLoading) {
      return (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Card className={classes.card} style={{ height: '100%' }}>
                <CardContent className={classes.cardContent}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" height={40} style={{ marginTop: 8, marginBottom: 8 }} />
                  <Skeleton variant="text" width="30%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      );
    }

    if (overviewError) {
      return (
        <Card className={`${classes.card} ${classes.errorCard}`}>
          <CardContent className={classes.cardContent}>
            <Box display="flex" alignItems="center" justifyContent="center" style={{ minHeight: 100 }}>
              <Typography color="error">{overviewError}</Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    const comparisonSubtitle = getComparisonLabel();
    const nouveauxPeriode = overview?.nouveaux_comptes_periode || 0;
    const periodeSuffix = periodLabel ? ` cette ${periodLabel}` : '';

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Comptes créés depuis le début"
            value={overview?.total_comptes_lifetime}
            subtitle={`+${nouveauxPeriode}${periodeSuffix}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Ont commandé au moins 1x"
            value={overview?.total_avec_commande_lifetime}
            subtitle={`sur ${overview?.total_comptes_lifetime || 0} comptes depuis le début`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Utilisateurs qui ont commandé"
            value={overview?.utilisateurs_actifs?.value}
            trend={overview?.utilisateurs_actifs?.variation}
            trendDirection={overview?.utilisateurs_actifs?.variation >= 0 ? 'up' : 'down'}
            subtitle={comparisonSubtitle}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={periodLabel ? `Commandes/${periodLabel}` : 'Commandes'}
            value={overview?.commandes_periode?.value}
            trend={overview?.commandes_periode?.variation}
            trendDirection={overview?.commandes_periode?.variation >= 0 ? 'up' : 'down'}
            subtitle={comparisonSubtitle}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title={periodLabel ? `Commission/${periodLabel}` : 'Commission'}
            value={overview?.commission_periode?.value}
            unit="€"
            trend={overview?.commission_periode?.variation}
            trendDirection={overview?.commission_periode?.variation >= 0 ? 'up' : 'down'}
            subtitle={comparisonSubtitle}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="ARPU (commission)"
            value={overview?.arpu?.value}
            unit="€"
            trend={overview?.arpu?.variation}
            trendDirection={overview?.arpu?.variation >= 0 ? 'up' : 'down'}
            subtitle={comparisonSubtitle}
          />
        </Grid>
      </Grid>
    );
  };

  const renderAcquisitionSection = () => {
    if (acquisitionLoading) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card className={classes.card} style={{ height: '100%' }}>
              <CardContent className={classes.cardContent}>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="rect" height={200} style={{ marginTop: 16, borderRadius: 8 }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card className={classes.card} style={{ height: '100%' }}>
              <CardContent className={classes.cardContent}>
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="circle" width={150} height={150} style={{ margin: '16px auto' }} />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card className={classes.card} style={{ height: '100%' }}>
              <CardContent className={classes.cardContent}>
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="rect" height={60} style={{ marginTop: 16, borderRadius: 8 }} />
                <Skeleton variant="rect" height={60} style={{ marginTop: 8, borderRadius: 8 }} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      );
    }

    if (acquisitionError) {
      return (
        <Card className={`${classes.card} ${classes.errorCard}`}>
          <CardContent className={classes.cardContent}>
            <Box display="flex" alignItems="center" justifyContent="center" style={{ minHeight: 100 }}>
              <Typography color="error">{acquisitionError}</Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    const totalNouveaux = acquisition?.total_nouveaux?.value ?? 0;
    const tauxConversion = acquisition?.taux_conversion?.value ?? 0;
    const commandeurs = Math.round((totalNouveaux * tauxConversion) / 100);

    // Fusionner les données nouveaux comptes et premières commandes
    const acquisitionChartData = (acquisition?.nouveaux_comptes || []).map((item, index) => ({
      date: item.date,
      nouveaux_comptes: item.count,
      premieres_commandes: acquisition?.premieres_commandes?.[index]?.count || 0
    }));

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TrendChart
            data={acquisitionChartData}
            title="Acquisition"
            series={[
              { dataKey: 'nouveaux_comptes', color: '#1976d2', name: 'Nouveaux comptes' },
              { dataKey: 'premieres_commandes', color: '#4caf50', name: 'Premières commandes' }
            ]}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FunnelChart
            stages={[
              { name: 'Inscrits', value: totalNouveaux },
              { name: 'Utilisateurs qui ont commandé', value: commandeurs }
            ]}
          />
        </Grid>
        <Grid item xs={12}>
          {acquisitionCohortLoading ? (
            <Card className={classes.card}>
              <CardContent className={classes.cardContent}>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="rect" height={200} style={{ marginTop: 16, borderRadius: 8 }} />
                <Skeleton variant="rect" height={150} style={{ marginTop: 16, borderRadius: 8 }} />
              </CardContent>
            </Card>
          ) : acquisitionCohortError ? (
            <Card className={`${classes.card} ${classes.errorCard}`}>
              <CardContent className={classes.cardContent}>
                <Box display="flex" alignItems="center" justifyContent="center" style={{ minHeight: 100 }}>
                  <Typography color="error">{acquisitionCohortError}</Typography>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <CohortRetentionCard data={acquisitionCohort} />
          )}
        </Grid>
        <Grid item xs={12}>
          {monthlyCohortLoading ? (
            <Card className={classes.card}>
              <CardContent className={classes.cardContent}>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="rect" height={250} style={{ marginTop: 16, borderRadius: 8 }} />
              </CardContent>
            </Card>
          ) : monthlyCohortError ? (
            <Card className={`${classes.card} ${classes.errorCard}`}>
              <CardContent className={classes.cardContent}>
                <Box display="flex" alignItems="center" justifyContent="center" style={{ minHeight: 100 }}>
                  <Typography color="error">{monthlyCohortError}</Typography>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <MonthlyCohortTable data={monthlyCohort} />
          )}
        </Grid>
      </Grid>
    );
  };

  const renderRetentionSection = () => {
    if (retentionLoading) {
      return (
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Card className={classes.card} style={{ height: '100%' }}>
                <CardContent className={classes.cardContent}>
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" height={40} style={{ marginTop: 8, marginBottom: 8 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      );
    }

    if (retentionError) {
      return (
        <Card className={`${classes.card} ${classes.errorCard}`}>
          <CardContent className={classes.cardContent}>
            <Box display="flex" alignItems="center" justifyContent="center" style={{ minHeight: 100 }}>
              <Typography color="error">{retentionError}</Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    // Calculer le nombre total de commandes cumulées
    const dist = retention?.distribution_frequence || {};
    const totalCommandesCumulees =
      (dist['1_commande'] || 0) * 1 +
      (dist['2_3_commandes'] || 0) * 2.5 +
      (dist['4_5_commandes'] || 0) * 4.5 +
      (dist['6_10_commandes'] || 0) * 8 +
      (dist['plus_10_commandes'] || 0) * 12;
    const totalCommandesArrondi = Math.round(totalCommandesCumulees);

    // Préparer les données pour le graphique temporel
    const commandesTemporellesData = (retention?.commandes_temporelles || []).map(item => ({
      date: item.date,
      commandes: item.nb_commandes
    }));

    // Déterminer le label de la granularité
    const getGranularityLabel = () => {
      if (!commandesTemporellesData.length) return '';
      const totalDays = customDates?.startDate && customDates?.endDate
        ? Math.ceil((new Date(customDates.endDate) - new Date(customDates.startDate)) / (1000 * 60 * 60 * 24))
        : 7;
      if (totalDays <= 14) return 'par jour';
      if (totalDays <= 90) return 'par semaine';
      return 'par mois';
    };

    return (
      <>
        <Typography className={classes.sectionTitle} style={{ marginTop: 24 }}>Fréquence d'usage</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4} md={3}>
            <KPICard
              title="Commandes cumulées"
              value={totalCommandesArrondi}
              subtitle="sur la période"
            />
          </Grid>
          <Grid item xs={12} sm={8} md={9}>
            <FrequencyBarChart
              distribution={retention?.distribution_frequence}
              title="Distribution des fréquences"
            />
          </Grid>
          {/* Graphique temporel des commandes - visible en période personnalisée, mois ou trimestre */}
          {(period === 'custom' || period === 'month' || period === 'quarter') && commandesTemporellesData.length > 0 && (
            <>
              <Grid item xs={12} sm={4} md={3}>
                <KPICard
                  title="Moyenne commandes"
                  value={retention?.moyenne_commandes_periode || 0}
                  subtitle={getGranularityLabel()}
                />
              </Grid>
              <Grid item xs={12} sm={8} md={9}>
                <TrendChart
                  data={commandesTemporellesData}
                  title={`Évolution des commandes ${getGranularityLabel()}`}
                  series={[
                    { dataKey: 'commandes', color: '#1976d2', name: 'Commandes' }
                  ]}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <KPICard
                  title="Commandes / utilisateur"
                  value={retention?.moyenne_commandes_par_utilisateur || 0}
                  subtitle="moyenne sur la période"
                />
              </Grid>
              <Grid item xs={12} sm={8} md={9}>
                <TrendChart
                  data={(retention?.commandes_par_utilisateur_temporel || []).map(item => ({
                    date: item.date,
                    moyenne: item.moyenne_par_utilisateur
                  }))}
                  title={`Moyenne commandes/utilisateur ${getGranularityLabel()}`}
                  series={[
                    { dataKey: 'moyenne', color: '#4caf50', name: 'Commandes/utilisateur' }
                  ]}
                />
              </Grid>
            </>
          )}
        </Grid>
      </>
    );
  };

  const renderValueSection = () => {
    if (valueLoading) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4} md={3}>
            <Card className={classes.card} style={{ height: '100%' }}>
              <CardContent className={classes.cardContent}>
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="40%" height={40} style={{ marginTop: 8, marginBottom: 8 }} />
                <Skeleton variant="text" width="30%" />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={8} md={9}>
            <Card className={classes.card} style={{ height: '100%' }}>
              <CardContent className={classes.cardContent}>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="rect" height={200} style={{ marginTop: 16, borderRadius: 8 }} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      );
    }

    if (valueError) {
      return (
        <Card className={`${classes.card} ${classes.errorCard}`}>
          <CardContent className={classes.cardContent}>
            <Box display="flex" alignItems="center" justifyContent="center" style={{ minHeight: 100 }}>
              <Typography color="error">{valueError}</Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    const chartData = valueData?.ca_periode?.map((item, index) => ({
      date: item.date,
      ca: item.value,
      commission: valueData?.commission_periode?.[index]?.value || 0
    })) || [];

    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TrendChart
            data={chartData}
            series={[
              { dataKey: 'ca', color: '#1976d2', name: 'CA' },
              { dataKey: 'commission', color: '#4caf50', name: 'Commission (10%)' }
            ]}
            title="CA et Commission"
            formatValue={formatCurrency}
          />
        </Grid>
      </Grid>
    );
  };

  const renderRestaurantsSection = () => {
    if (restaurantsLoading) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4} md={3}>
            <Card style={{ height: '100%' }}>
              <CardContent>
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="40%" height={40} style={{ marginTop: 8, marginBottom: 8 }} />
                <Skeleton variant="text" width="30%" />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={8} md={9}>
            <Card style={{ height: '100%' }}>
              <CardContent>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="rect" height={300} style={{ marginTop: 16 }} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      );
    }

    if (restaurantsError) {
      return (
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="center" style={{ minHeight: 100 }}>
              <Typography color="error">{restaurantsError}</Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    const restaurantsComparisonSubtitle = getComparisonLabel(restaurants);

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4} md={3}>
          <Card style={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Total établissements
              </Typography>
              <Typography variant="h4" style={{ marginBottom: 16 }}>
                {restaurants?.total_etablissements || 0}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Établissements actifs
              </Typography>
              <Typography variant="h4">
                {restaurants?.etablissements_actifs?.value || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={8} md={9}>
          <RankingTable
            title="Tous les Établissements"
            subtitle={restaurantsComparisonSubtitle}
            data={restaurants?.top_10 || []}
            onRowClick={handleRestaurantClick}
          />
        </Grid>
      </Grid>
    );
  };

  const renderB2BSection = () => {
    if (b2bLoading) {
      return (
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4} md={3}>
            <Card style={{ height: '100%' }}>
              <CardContent>
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="text" width="40%" height={40} style={{ marginTop: 8, marginBottom: 8 }} />
                <Skeleton variant="text" width="30%" />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={8} md={9}>
            <Card style={{ height: '100%' }}>
              <CardContent>
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="rect" height={300} style={{ marginTop: 16 }} />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      );
    }

    if (b2bError) {
      return (
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="center" style={{ minHeight: 100 }}>
              <Typography color="error">{b2bError}</Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    const b2bComparisonSubtitle = getComparisonLabel(b2bData);

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4} md={3}>
          <KPICard
            title="Entreprises actives"
            value={b2bData?.entreprises_actives?.value || 0}
          />
        </Grid>
        <Grid item xs={12} sm={8} md={9}>
          <AdoptionTable data={b2bData?.adoption || []} subtitle={b2bComparisonSubtitle} />
        </Grid>
      </Grid>
    );
  };

  return (
    <div className={classes.root}>
      {/* Navbar */}
      <Navbar />

      <div className={classes.container} ref={dashboardRef}>
        {/* Header avec titre, sélecteur et bouton */}
        <Box className={classes.header}>
          <Typography className={classes.headerTitle}>
            <span role="img" aria-label="chart">📊</span>
            Dashboard Csibon
          </Typography>
          <Box className={classes.headerActions}>
            <PeriodSelector
              value={period}
              onChange={handlePeriodChange}
              customDates={customDates}
              onCustomDatesChange={handleCustomDatesChange}
            />
            <Button
              variant="contained"
              className={classes.pdfButton}
              startIcon={exportingPdf ? <CircularProgress size={20} color="inherit" /> : <GetAppIcon />}
              onClick={handleExportPdf}
              disabled={exportingPdf || loading}
            >
              Télécharger PDF
            </Button>
            <Button
              variant="contained"
              className={classes.refreshButton}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Actualiser
            </Button>
          </Box>
        </Box>

        {/* Message erreur global */}
        {error && (
          <Card className={`${classes.card} ${classes.errorCard}`} style={{ marginBottom: 24 }}>
            <CardContent>
              <Typography color="error">{error}</Typography>
            </CardContent>
          </Card>
        )}

        {/* Section Vue d'Ensemble - 5 KPIs */}
        <Box display="flex" alignItems="baseline" gap={2} marginBottom={2}>
          <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>Vue d'ensemble</Typography>
          {formatPeriodDates() && (
            <Typography variant="body2" color="textSecondary">
              ({formatPeriodDates()})
            </Typography>
          )}
        </Box>
        {renderOverviewSection()}

        {/* Section Acquisition et Retention */}
        <Typography className={classes.sectionTitle}>Acquisition et Retention</Typography>
        {renderAcquisitionSection()}
        {renderRetentionSection()}

        {/* Section Valeur */}
        <Typography className={classes.sectionTitle}>Valeur</Typography>
        {renderValueSection()}

        {/* Section Restaurateurs */}
        <Typography className={classes.sectionTitle}>Restaurateurs</Typography>
        {renderRestaurantsSection()}

        {/* Section Entreprises B2B */}
        <Typography className={classes.sectionTitle}>Entreprises B2B</Typography>
        {renderB2BSection()}
      </div>

      {/* Modal détails établissement */}
      <Dialog
        open={detailsModalOpen}
        onClose={handleCloseDetailsModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {detailsData?.etablissement?.name || 'Détails établissement'}
            </Typography>
            <IconButton onClick={handleCloseDetailsModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailsLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : detailsData ? (
            <>
              {/* Totaux */}
              <Box mb={3}>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Totaux période
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="textSecondary">CA Sur place</Typography>
                    <Typography variant="h6">{formatCurrency(detailsData.totaux?.ca_sur_place || 0)}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="textSecondary">CA Livraison</Typography>
                    <Typography variant="h6">{formatCurrency(detailsData.totaux?.ca_livraison || 0)}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="textSecondary">CA Total</Typography>
                    <Typography variant="h6">{formatCurrency(detailsData.totaux?.ca_total || 0)}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="textSecondary">Marge nette (10%)</Typography>
                    <Typography variant="h6">
                      {formatCurrency((detailsData.totaux?.ca_total || 0) * 0.1)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Tableau entreprises */}
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                CA par entreprise
              </Typography>
              <TableContainer style={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Entreprise</TableCell>
                      <TableCell align="right">Commandes</TableCell>
                      <TableCell align="right">CA Sur place</TableCell>
                      <TableCell align="right">CA Livraison</TableCell>
                      <TableCell align="right">CA Total</TableCell>
                      <TableCell align="right">Marge nette (10%)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailsData.entreprises?.length > 0 ? (
                      detailsData.entreprises.map((entreprise) => (
                        <TableRow key={entreprise.entreprise_id}>
                          <TableCell>{entreprise.nom}</TableCell>
                          <TableCell align="right">{entreprise.nb_commandes}</TableCell>
                          <TableCell align="right">{formatCurrency(entreprise.ca_sur_place)}</TableCell>
                          <TableCell align="right">{formatCurrency(entreprise.ca_livraison)}</TableCell>
                          <TableCell align="right">{formatCurrency(entreprise.ca_total)}</TableCell>
                          <TableCell align="right">
                            {formatCurrency((entreprise.ca_total || 0) * 0.1)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="textSecondary">
                            Aucune entreprise n'a commandé dans cet établissement
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Typography color="error">Erreur lors du chargement des données</Typography>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardView;
